import {
  VARIANCE_REPORT_MODE,
  VarianceReportModeType,
} from "constants/financialClosing";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useState } from "react";
import { useGetVarianceExplanationQuery } from "store/apis/reports";
import { VarianceExplanation } from "types/Models/report";
import { ReportFilterType } from "../ReportFilter";
import { VarianceExplanationContent } from "./VarianceExplanationContent";

type VarianceExplanationCellProps = {
  categoryUuid: string | null;
  syntheticRowName: string | null;
  reportType: string;
  filter: ReportFilterType;
  mode: VarianceReportModeType;
  regenerateCount: number;
  storedExplanation?: VarianceExplanation;
};

export const VarianceExplanationCell = ({
  categoryUuid,
  syntheticRowName,
  reportType,
  filter,
  mode,
  regenerateCount,
  storedExplanation,
}: VarianceExplanationCellProps) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const [hasRequestedRetry, setHasRequestedRetry] = useState(false);

  const hasStoredExplanation = Boolean(storedExplanation);
  const isRegenerating = mode === VARIANCE_REPORT_MODE.REGENERATE;
  const isGenerating = mode === VARIANCE_REPORT_MODE.GENERATE;

  const shouldRenderStoredExplanation =
    mode === VARIANCE_REPORT_MODE.VIEW &&
    hasStoredExplanation &&
    !hasRequestedRetry;
  const shouldFetch = isGenerating || isRegenerating || hasRequestedRetry;

  const { data, isFetching, isLoading, isError, refetch } =
    useGetVarianceExplanationQuery(
      {
        groupId,
        entityId,
        categoryUuid,
        syntheticRowName,
        reportType,
        startDate: filter.START_DATE || null,
        endDate: filter.END_DATE || null,
        accountingMethod: filter.ACCOUNTING_METHOD,
        comparisonReportType: filter.COMPARE_BY,
        showNonZeroAccounts: filter.HIDE_ZERO,
        forceRegenerate: isRegenerating || hasRequestedRetry,
        regenerateCount,
      }
      // { skip: !shouldFetch }
    );

  if (shouldRenderStoredExplanation) {
    return (
      <div className="t-w-full t-max-w-[320px] t-whitespace-normal t-py-1">
        <VarianceExplanationContent
          isLoading={false}
          isError={false}
          explanation={storedExplanation}
          onRetry={() => {}}
        />
      </div>
    );
  }

  const isMissing = !hasStoredExplanation && !shouldFetch;
  const handleRetry = () => {
    if (isMissing) {
      setHasRequestedRetry(true);
      return;
    }
    refetch();
  };

  return (
    <div className="t-w-full t-max-w-[320px] t-whitespace-normal t-py-1">
      <VarianceExplanationContent
        isLoading={isLoading || isFetching}
        isError={Boolean(isError)}
        explanation={data}
        onRetry={handleRetry}
      />
    </div>
  );
};
